import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { Toaster } from './components/ui/sonner';

// Auth
import Login from './pages/auth/Login';
import PublicRegistration from './pages/PublicRegistration';
import VerifyEmail from './pages/VerifyEmail';
import VerifyOTP from './pages/VerifyOTP';

// Admin
import AdminDashboard from './pages/admin/Dashboard';
import QuestionBanks from './pages/admin/QuestionBanks';
import QuestionBankDetail from './pages/admin/QuestionBankDetail';
import CreateQuestion from './pages/admin/CreateQuestion';
import GenerateAIQuestions from './pages/admin/GenerateAIQuestions';
import Tests from './pages/admin/Tests';
import CreateTest from './pages/admin/CreateTest';
import CreateUser from './pages/admin/CreateUser';
import TestDetail from './pages/admin/TestDetail';
import Users from './pages/admin/Users';
import Batches from './pages/admin/Batches';
import Analytics from './pages/admin/AnalyticsNew';
import AuditLogs from './pages/admin/AuditLogs';
import CleanupAttempts from './pages/admin/CleanupAttempts';
import BatchRankings from './pages/admin/BatchRankingsNew';
import TestRankings from './pages/admin/TestRankings';
import PublicRegistrations from './pages/admin/PublicRegistrations';
import PsychometricAdmin from './pages/admin/PsychometricAdmin';
import PsychometricDetail from './pages/admin/PsychometricDetail';
import ProctoringDashboard from './pages/admin/ProctoringDashboard';
import FacultyReports from './pages/admin/FacultyReports';
import GoogleDriveSettings from './pages/admin/GoogleDriveSettings';
import Settings from './pages/admin/Settings';
import MobileProctor from './pages/MobileProctor';

// Interview Module
import InterviewAdminDashboard from './pages/interview/InterviewAdminDashboard';
import StudentGate from './pages/interview/StudentGate';
import EnvironmentCheck from './pages/interview/EnvironmentCheck';
import LiveTest from './pages/interview/LiveTest';
import TestComplete from './pages/interview/TestComplete';
import AdminAttemptGrade from './pages/interview/AdminAttemptGrade';
import MobileEnvScan from './pages/interview/MobileEnvScan';

// User
import UserDashboard from './pages/user/Dashboard';
import TakeTest from './pages/user/TakeTest';
import TestResults from './pages/user/TestResults';
import TakePsychometricTest from './pages/user/TakePsychometricTest';
import PsychometricResults from './pages/user/PsychometricResults';

// Other
import NotFound from './pages/NotFound';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, user, loading }) => {
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && !['admin', 'super_admin', 'faculty'].includes(user.role)) return <Navigate to="/dashboard" />;
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load initial user from localStorage on mount
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const initialUser = token && userData ? JSON.parse(userData) : null;
    setUser(initialUser);
    setLoading(false);
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/register" element={<PublicRegistration />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-otp" element={<VerifyOTP />} />
          <Route path="/mobile-proctor/:token" element={<MobileProctor />} />

          {/* Phone-side 360° env-scan handoff (QR target from EnvironmentCheck) */}
          <Route path="/env-scan/:handoffToken" element={<MobileEnvScan />} />
          
          {/* Interview Module — Public student routes (token-based auth) */}
          <Route path="/interview/gate/:token" element={<StudentGate />} />
          <Route path="/interview/env-check" element={<EnvironmentCheck />} />
          <Route path="/interview/live" element={<LiveTest />} />
          <Route path="/interview/complete" element={<TestComplete />} />
          <Route path="/interview/mobile-scan/:handoffId" element={<MobileEnvScan />} />
          {/* Short code + JWT routes from interview invite links */}
          <Route path="/s/:code" element={<StudentGate />} />
          <Route path="/exam/s/:code" element={<StudentGate />} />
          <Route path="/exam/:token" element={<StudentGate />} />
          <Route path="/exam/:token/env" element={<EnvironmentCheck />} />
          <Route path="/exam/:token/live" element={<LiveTest />} />
          <Route path="/exam/:token/complete" element={<TestComplete />} />
          <Route path="/exam/:token/mobile-scan/:handoffId" element={<MobileEnvScan />} />
          
          <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login setUser={setUser} />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<ProtectedRoute adminOnly user={user} loading={loading}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/question-banks" element={<ProtectedRoute adminOnly user={user} loading={loading}><QuestionBanks /></ProtectedRoute>} />
          <Route path="/admin/question-banks/:bankId" element={<ProtectedRoute adminOnly user={user} loading={loading}><QuestionBankDetail /></ProtectedRoute>} />
          <Route path="/admin/questions/create" element={<ProtectedRoute adminOnly user={user} loading={loading}><CreateQuestion /></ProtectedRoute>} />
          <Route path="/admin/questions/generate-ai" element={<ProtectedRoute adminOnly user={user} loading={loading}><GenerateAIQuestions /></ProtectedRoute>} />
          <Route path="/admin/tests" element={<ProtectedRoute adminOnly user={user} loading={loading}><Tests /></ProtectedRoute>} />
          <Route path="/admin/tests/create" element={<ProtectedRoute adminOnly user={user} loading={loading}><CreateTest /></ProtectedRoute>} />
          <Route path="/admin/tests/:testId" element={<ProtectedRoute adminOnly user={user} loading={loading}><TestDetail /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute adminOnly user={user} loading={loading}><Users /></ProtectedRoute>} />
          <Route path="/admin/users/create" element={<ProtectedRoute adminOnly user={user} loading={loading}><CreateUser /></ProtectedRoute>} />
          <Route path="/admin/batches" element={<ProtectedRoute adminOnly user={user} loading={loading}><Batches /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute adminOnly user={user} loading={loading}><Analytics /></ProtectedRoute>} />
          <Route path="/admin/audit-logs" element={<ProtectedRoute adminOnly user={user} loading={loading}><AuditLogs /></ProtectedRoute>} />
          <Route path="/admin/cleanup-attempts" element={<ProtectedRoute adminOnly user={user} loading={loading}><CleanupAttempts /></ProtectedRoute>} />
          <Route path="/admin/rankings" element={<ProtectedRoute adminOnly user={user} loading={loading}><BatchRankings /></ProtectedRoute>} />
          <Route path="/admin/test-rankings" element={<ProtectedRoute adminOnly user={user} loading={loading}><TestRankings /></ProtectedRoute>} />
          <Route path="/admin/public-registrations" element={<ProtectedRoute adminOnly user={user} loading={loading}><PublicRegistrations /></ProtectedRoute>} />
          <Route path="/admin/psychometric" element={<ProtectedRoute adminOnly user={user} loading={loading}><PsychometricAdmin /></ProtectedRoute>} />
          <Route path="/admin/psychometric/detail/:attemptId" element={<ProtectedRoute adminOnly user={user} loading={loading}><PsychometricDetail /></ProtectedRoute>} />
          <Route path="/admin/proctoring" element={<ProtectedRoute adminOnly user={user} loading={loading}><ProctoringDashboard /></ProtectedRoute>} />
          <Route path="/admin/faculty-reports" element={<ProtectedRoute adminOnly user={user} loading={loading}><FacultyReports /></ProtectedRoute>} />
          <Route path="/admin/google-drive" element={<ProtectedRoute adminOnly user={user} loading={loading}><GoogleDriveSettings /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute adminOnly user={user} loading={loading}><Settings /></ProtectedRoute>} />
          
          {/* Interview Admin Routes */}
          <Route path="/admin/interview" element={<ProtectedRoute adminOnly user={user} loading={loading}><InterviewAdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/interview/grade/:attemptId" element={<ProtectedRoute adminOnly user={user} loading={loading}><AdminAttemptGrade /></ProtectedRoute>} />
          
          {/* User Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute user={user} loading={loading}>
              {['admin', 'super_admin', 'faculty'].includes(user?.role) ? <Navigate to="/admin" /> : <UserDashboard />}
            </ProtectedRoute>
          } />
          <Route path="/test/:testId" element={<ProtectedRoute user={user} loading={loading}><TakeTest /></ProtectedRoute>} />
          <Route path="/results/:attemptId" element={<ProtectedRoute user={user} loading={loading}><TestResults /></ProtectedRoute>} />
          <Route path="/psychometric/:testId" element={<ProtectedRoute user={user} loading={loading}><TakePsychometricTest /></ProtectedRoute>} />
          <Route path="/psychometric-results/:attemptId" element={<ProtectedRoute user={user} loading={loading}><PsychometricResults /></ProtectedRoute>} />
          
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </>
  );
}

export default App;